// tinker: gravity — feel home's pull. A drone hums on home, the whole piano
// is playable (pointer + typing + MIDI), and a tension meter eases to the
// tension of the most recent held note. When every key is released the
// needle settles back toward rest. Always ready to continue.
//
// Audio can't start without a user gesture, so a "begin" tap resumes the
// context and fades the drone in before the piano goes live.
//
// Unit 6: config.home: 'minor' moves home to E — the drone hums on E
// (startDrone(40)), degrees come from minorDegreeOf, and the tension table
// swaps to the minor one (the natural 7 barely pulls). Needle behavior is
// unchanged.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { degreeOf, minorDegreeOf } from "../../../lib/homeKeyTheory";
import { PATCHES, startDrone, resumeAudio } from "../../../lib/homeKeyAudio";
import Piano from "../Piano";
import useHomeKeyVoices from "../useHomeKeyVoices";
import useTypingKeys from "../useTypingKeys";
import useHomeKeyMidi from "../useHomeKeyMidi";

// Per-degree tension (spec table): home rests, the doorbell rings hardest,
// outside-the-scale notes sit high but not maximal.
const DEFAULT_TENSION = { 1: 0.05, 2: 0.55, 3: 0.2, 4: 0.65, 5: 0.25, 6: 0.45, 7: 0.9 };
// Minor gravity (Unit 6 spec table): the shadow home rests, the natural 7 —
// "the old road" — pulls hardest of the scale tones but never doorbell-hard.
const MINOR_TENSION = { 1: 0.05, 2: 0.55, 3: 0.3, 4: 0.5, 5: 0.35, 6: 0.6, 7: 0.7 };
const DEFAULT_OUT_TENSION = 0.8;

export default function TinkerGravity({ moment, onReadyChange }) {
  const config = moment.config || {};
  const isMinor = config.home === "minor";
  const table = config.tension || (isMinor ? MINOR_TENSION : DEFAULT_TENSION);
  const outTension =
    (config.tension && config.tension.out) != null
      ? config.tension.out
      : config.outOfScaleTension != null
      ? config.outOfScaleTension
      : DEFAULT_OUT_TENSION;
  const restTension = table[1] != null ? table[1] : 0.05;

  const [begun, setBegun] = useState(false);
  const [tension, setTension] = useState(restTension);
  const [lastDegree, setLastDegree] = useState(null); // null = nothing yet, 0 = outside
  const droneRef = useRef(null);

  const voices = useHomeKeyVoices(PATCHES.softKeys);
  const { press, release, activeMidis } = voices;

  useEffect(() => {
    if (onReadyChange) onReadyChange(true);
  }, [onReadyChange]);

  const stopAll = useCallback(() => {
    if (droneRef.current) droneRef.current.stop();
    droneRef.current = null;
  }, []);
  useEffect(() => () => stopAll(), [stopAll]);

  const handlePress = useCallback(
    (midi) => {
      press(midi);
      const degree = isMinor ? minorDegreeOf(midi) : degreeOf(midi);
      setLastDegree(degree || 0);
      setTension(degree ? table[degree] : outTension);
    },
    [press, table, outTension, isMinor]
  );
  const handleRelease = useCallback((midi) => release(midi), [release]);

  // When nothing is held, let the needle settle back toward rest.
  useEffect(() => {
    if (begun && activeMidis.size === 0) setTension(restTension);
  }, [begun, activeMidis, restTension]);

  useTypingKeys(handlePress, handleRelease, begun);
  const midi = useHomeKeyMidi(handlePress, handleRelease, begun);

  const begin = () => {
    resumeAudio();
    if (!droneRef.current) {
      droneRef.current = isMinor ? startDrone(40) : startDrone();
    }
    setBegun(true);
  };

  const feltAs =
    lastDegree == null
      ? "play a note and watch the needle"
      : lastDegree === 0
      ? "outside the scale — pulling hard"
      : lastDegree === 1
      ? "home — at rest"
      : `degree ${lastDegree}`;

  return (
    <div className="hk-moment-body">
      <p>
        {config.prompt ||
          (isMinor
            ? "Home has moved. The drone hums on E now — wander the same keys and feel how every note points somewhere new."
            : "The drone is home, humming. Wander the keys and watch how far each note pulls you from it — then feel every note tug back toward G.")}
      </p>

      {!begun ? (
        <button type="button" className="hk-btn hk-btn--primary" onClick={begin}>
          begin — fade the drone in
        </button>
      ) : (
        <>
          <div
            className="hk-tension-meter"
            role="img"
            aria-label={`tension ${Math.round(tension * 100)} percent — ${feltAs}`}
          >
            <div
              className="hk-tension-fill"
              style={{
                width: `${Math.round(tension * 100)}%`,
                transition: "width 450ms ease",
              }}
            />
          </div>
          <p className="hk-tension-caption">{feltAs}</p>
        </>
      )}

      <Piano
        activeMidis={activeMidis}
        onPress={handlePress}
        onRelease={handleRelease}
        disabled={!begun}
      />
      {midi.connected && (
        <p className="hk-midi-note">MIDI connected: {midi.deviceNames.join(", ")}</p>
      )}
    </div>
  );
}
